import React from "react";
import NextHead from "next/head";
import { useSelector } from "react-redux";
import { SEODataType } from "../global";
import { getIsDarkMode } from "../store/slices/webConfig";

interface HeadProps {
  seoData: SEODataType;
}

function Head({ seoData }: HeadProps) {
  const isDarkMode = useSelector(getIsDarkMode);
  const isSchemaPresent = Array.isArray(seoData?.schema) && seoData?.schema?.length > 0;
  const schemas = isSchemaPresent ? seoData?.schema : [];
  const headSchemaMarkups = schemas.filter((schema) => schema.location !== "body");
  const isDevelopment = process.env.NODE_ENV === "development";


  return (
    <NextHead>
      <meta charSet="UTF-8" />
      <title>{seoData?.meta?.title || ""}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <link rel="preconnect" href="https://cdn.moengage.com/" crossorigin />
      <link rel="dns-prefetch" href="https://cdn.moengage.com/" />
      <link rel="preconnect" href="https://sdk-03.moengage.com/" crossorigin />
      <link rel="dns-prefetch" href="https://sdk-03.moengage.com/" />
      <script
        src={`https://cdn.moengage.com/release/dc_3/moe_webSdk_webp.min.latest.js?app_id=YFOYJ31I34XKYOAQC1FGA6R9&debug_logs=${process.env.NEXT_PUBLIC_MOENGAGE_DEBUG_LOG}`}
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!=='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-KL29CZ4');`
        }}
        defer
      />

      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `var moeDataCenter = "dc_3"; // Replace "DC" with the actual Data center value from the above table
var moeAppID = "YFOYJ31I34XKYOAQC1FGA6R9"; // Replace "WorkspaceID" available in the settings page of MoEngage Dashboard.
var sdkVersion = "2"; // Replace this value with the version of Web SDK that you intend to use. It is recommended to use the format x (major)
      
!function(e,n,i,t,a,r,o,d){if(!moeDataCenter||!moeDataCenter.match(/^dc_[0-9]+$/gm))return console.error("Data center has not been passed correctly. Please follow the SDK installation instruction carefully.");var s=e[a]=e[a]||[];if(s.invoked=0,s.initialised>0||s.invoked>0)return console.error("MoEngage Web SDK initialised multiple times. Please integrate the Web SDK only once!"),!1;e.moengage_object=a;var l={},g=function n(i){return function(){for(var n=arguments.length,t=Array(n),a=0;a<n;a++)t[a]=arguments[a];(e.moengage_q=e.moengage_q||[]).push({f:i,a:t})}},u=["track_event","add_user_attribute","add_first_name","add_last_name","add_email","add_mobile","add_user_name","add_gender","add_birthday","destroy_session","add_unique_user_id","update_unique_user_id","moe_events","call_web_push","track","location_type_attribute"],m={onsite:["getData","registerCallback"]};for(var c in u)l[u[c]]=g(u[c]);for(var v in m)for(var f in m[v])null==l[v]&&(l[v]={}),l[v][m[v][f]]=g(v+"."+m[v][f]);r=n.createElement(i),o=n.getElementsByTagName("head")[0],r.async=1,r.src=t,o.appendChild(r),e.moe=e.moe||function(){return(s.invoked=s.invoked+1,s.invoked>1)?(console.error("MoEngage Web SDK initialised multiple times. Please integrate the Web SDK only once!"),!1):(d=arguments.length<=0?void 0:arguments[0],l)},r.addEventListener("load",function(){if(d)return e[a]=e.moe(d),e[a].initialised=e[a].initialised+1||1,!0}),r.addEventListener("error",function(){return console.error("Moengage Web SDK loading failed."),!1})}(window,document,"script","https://cdn.moengage.com/release/"+moeDataCenter+"/versions/"+sdkVersion+"/moe_webSdk.min.latest.js","Moengage");
 
Moengage = moe({
  app_id: moeAppID,
  debug_logs: ${process.env.NEXT_PUBLIC_MOENGAGE_DEBUG_LOG},
   swPath: "/service-worker.js"
});`
        }}
        defer
      />

      <meta name="title" content={seoData?.meta?.title} />
      <meta name="description" content={seoData?.meta?.description} />
      <meta name="twitter:title" content={seoData?.meta?.twitter_title} />
      <meta name="twitter:description" content={seoData?.meta?.twitter_description} />
      <meta name="twitter:image" content={seoData?.meta?.twitter_image} />
      <meta name="og:title" content={seoData?.meta?.og_title} />
      <meta name="og:description" content={seoData?.meta?.og_description} />
      <meta name="og:image" content={seoData?.meta?.og_image} />

      {seoData?.keywords !== "" ? <meta name="keywords" content={seoData?.keywords} /> : ""}
      {seoData?.language !== "" ? <meta name="language" content={seoData?.language} /> : ""}
      <meta name="robots" content={isDevelopment ? "noindex, nofollow" : seoData?.robots || ""} />

      {seoData?.canonical !== "" ? <link rel="canonical" href={seoData?.canonical} /> : ""}

      {seoData?.custom_tag?.map((custom_meta: { metaKey: string; metaValue: string }) => (
        <meta key={custom_meta.metaValue} name={custom_meta.metaKey} content={custom_meta.metaValue} />
      ))}

      {/* preload fonts */}
      <link rel="preload" href="/fonts/Roboto-Bold.ttf" as="font" crossOrigin="" />
      <link rel="preload" href="/fonts/Roboto-BoldItalic.ttf" as="font" crossOrigin="" />
      <link rel="preload" href="/fonts/Roboto-Italic.ttf" as="font" crossOrigin="" />
      <link rel="preload" href="/fonts/Roboto-Medium.ttf" as="font" crossOrigin="" />
      <link rel="preload" href="/fonts/Roboto-MediumItalic.ttf" as="font" crossOrigin="" />
      <link rel="preload" href="/fonts/Roboto-Regular.ttf" as="font" crossOrigin="" />

      {/* favicon */}
      <link rel="icon" href={isDarkMode ? "/favicon-dark.ico" : "/favicon.ico"} id="favicon" />

      {headSchemaMarkups.map(({ schema }) => (
        <script key={schema} type="application/ld+json" dangerouslySetInnerHTML={{ __html: schema }} />
      ))}
    </NextHead>
  );
}

export default Head;
